// Light / dark theme. Three settings: 'system' (default — follows the
// device), 'light' and 'dark'. Remembered per user so a night-shift
// planner keeps dark while day staff keep light on the same browser.
import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/context/AuthContext';

const ThemeContext = createContext({ theme: 'system', resolved: 'light', setTheme: () => {}, cycle: () => {} });

const KEY = (uid) => `kanriva.theme.${uid || 'anon'}`;
const systemDark = () => window.matchMedia('(prefers-color-scheme: dark)').matches;

export const ThemeProvider = ({ children }) => {
  const { user } = useAuth();
  const uid = user?.id || user?.email || null;
  const [theme, setThemeState] = useState(() => localStorage.getItem(KEY(null)) || 'system');

  // Switching user (login / logout) swaps in that user's preference.
  useEffect(() => {
    setThemeState(localStorage.getItem(KEY(uid)) || localStorage.getItem(KEY(null)) || 'system');
  }, [uid]);

  const resolved = useMemo(() => (theme === 'system' ? (systemDark() ? 'dark' : 'light') : theme), [theme]);

  useEffect(() => {
    const apply = () => {
      const dark = theme === 'dark' || (theme === 'system' && systemDark());
      document.documentElement.classList.toggle('dark', dark);
      document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
    };
    apply();
    if (theme !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next);
    localStorage.setItem(KEY(uid), next);
    localStorage.setItem(KEY(null), next);
  }, [uid]);

  const cycle = useCallback(() => {
    setTheme(resolved === 'dark' ? 'light' : 'dark');
  }, [resolved, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, resolved, setTheme, cycle }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
